import filter from "lodash/filter";
import find from "lodash/find";
import flatMap from "lodash/flatMap";
import { OptionsConfiguration } from "../../types/customization/configuration";
import {
  Content,
  ContentEditablePicture,
  ContentPage,
  ContentPrint,
  ContentPrintQuantity,
} from "../../types/customization/content";
import {
  Template,
  TemplateAssetDefinition,
  TemplateBaseColorDefinition,
  TemplateCellColorDefinition,
  TemplateColorDefinition,
  TemplateEditablePictureDefinition,
  TemplateEditableTextDefinition,
  TemplateFontColorDefinition,
  TemplateFontDefinition,
  TemplateForcedImageDefinition,
  TemplateForcedTextDefinition,
  TemplateOptionDefinition,
  TemplatePageDefinition,
  TemplatePresentationDefinition,
} from "../../types/customization/template";
import { evalContentCondition } from "./condition";
import { hasEmptyEditablePictures } from "./page";

class ContentPrintQuantityNotFoundError extends Error {}
class TemplateAssetDefinitionNotFoundError extends Error {}
class TemplateFontDefinitionNotFoundError extends Error {}
class TemplateOptionDefinitionNotFoundError extends Error {}
class TemplatePageDefinitionNotFoundError extends Error {}
class TemplatePageNotFoundError extends Error {}
class TemplatePresentationDefinitionNotFoundError extends Error {}

export function findEditablePicture(
  editablePictureKey: string,
  page: ContentPage
): ContentEditablePicture {
  const editablePicture = find(
    page.editablePictures,
    (element) => element.editablePictureKey === editablePictureKey
  );

  if (!editablePicture) {
    throw new Error("editablePicture not found");
  }

  return editablePicture;
}

export function findEditablePictureDefinition(
  editablePictureKey: string,
  pageDefinition: TemplatePageDefinition
): TemplateEditablePictureDefinition {
  const elementDefinition = find(
    pageDefinition.elementDefinitions,
    ({ key, type }) => key === editablePictureKey && type === "editable-picture"
  );

  if (!elementDefinition || elementDefinition.type !== "editable-picture") {
    throw new Error("editablePictureDefinition not found");
  }

  return elementDefinition;
}

export function findEditableTextDefinition(
  editableTextKey: string,
  pageDefinition: TemplatePageDefinition
): TemplateEditableTextDefinition {
  const elementDefinition = find(
    pageDefinition.elementDefinitions,
    ({ key, type }) => key === editableTextKey && type === "editable-text"
  );

  if (!elementDefinition || elementDefinition.type !== "editable-text") {
    throw new Error("editableTextDefinition not found");
  }

  return elementDefinition;
}

export function findForcedImageDefinition(
  editableTextKey: string,
  pageDefinition: TemplatePageDefinition
): TemplateForcedImageDefinition {
  const elementDefinition = find(
    pageDefinition.elementDefinitions,
    ({ key, type }) => key === editableTextKey && type === "forced-image"
  );

  if (!elementDefinition || elementDefinition.type !== "forced-image") {
    throw new Error("forcedImageDefinition not found");
  }

  return elementDefinition;
}

export function findForcedTextDefinition(
  forcedTextKey: string,
  pageDefinition: TemplatePageDefinition
): TemplateForcedTextDefinition {
  const elementDefinition = find(
    pageDefinition.elementDefinitions,
    ({ key, type }) => key === forcedTextKey && type === "forced-text"
  );

  if (!elementDefinition || elementDefinition.type !== "forced-text") {
    throw new Error("forcedTextDefinition not found");
  }

  return elementDefinition;
}

export function findPage(pageKey: string, content: Content): ContentPage {
  const page = find(content.pages, ({ key }) => key === pageKey);

  if (!page) {
    throw new TemplatePageNotFoundError(`Page not found for pageKey "${pageKey}"`);
  }

  return page;
}

export function findOptionDefinition(
  optionDefinitionKey: string,
  template: Template
): TemplateOptionDefinition {
  const optionDefinition = find(
    template.optionDefinitions,
    ({ key }) => key === optionDefinitionKey
  );

  if (!optionDefinition) {
    throw new TemplateOptionDefinitionNotFoundError(
      `OptionDefinition not found for optionDefinitionKey "${optionDefinitionKey}"`
    );
  }

  return optionDefinition;
}

export function findPageDefinition(
  pageDefinitionKey: string,
  template: { pageDefinitions: TemplatePageDefinition[] }
): TemplatePageDefinition {
  const pageDefinition = find(template.pageDefinitions, ({ key }) => key === pageDefinitionKey);
  const pageDefinitionKeys = template.pageDefinitions.map((def) => def.key);

  if (!pageDefinition) {
    throw new TemplatePageDefinitionNotFoundError(
      `PageDefinition not found for pageDefinitionKey "${pageDefinitionKey}" ${pageDefinitionKeys.join(
        " "
      )}`
    );
  }

  return pageDefinition;
}

export function findFontDefinition(fontKey: string, template: Template): TemplateFontDefinition {
  const fontDefinition = find(template.fontDefinitions, ({ key }) => key === fontKey);

  if (!fontDefinition) {
    throw new TemplateFontDefinitionNotFoundError(
      `FontDefinition not found for fontKey "${fontKey}"`
    );
  }

  return fontDefinition;
}

export function findAssetDefinition(assetKey: string, template: Template): TemplateAssetDefinition {
  const assetDefinition = find(template.assetDefinitions, ({ key }) => key === assetKey);

  if (!assetDefinition) {
    throw new TemplateAssetDefinitionNotFoundError(
      `AssetDefinition not found for assetKey "${assetKey}"`
    );
  }

  return assetDefinition;
}

export function findPresentationDefinition(
  presentationKey: string,
  template: Template
): TemplatePresentationDefinition {
  const presentationDefinition = find(
    template.presentationDefinitions,
    ({ key }) => key === presentationKey
  );

  if (!presentationDefinition) {
    throw new TemplatePresentationDefinitionNotFoundError(
      `PresentationDefinition not found for presentationKey "${presentationKey}"`
    );
  }

  return presentationDefinition;
}

export function findPageAndDefinition(
  pageKey: string,
  template: Template,
  content: Content
): { page: ContentPage; pageDefinition: TemplatePageDefinition } {
  const page = findPage(pageKey, content);
  const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);

  return { page, pageDefinition };
}

export function findPrintQuantity(pageKey: string, content: ContentPrint): ContentPrintQuantity {
  const printQuantity = find(content.printQuantities, (pQ) => pQ.pageKey === pageKey);

  if (!printQuantity) {
    throw new ContentPrintQuantityNotFoundError(`PrintQuantity not found for pageKey "${pageKey}"`);
  }

  return printQuantity;
}

export function findCroppedPictures(pages: ContentPage[]): ContentEditablePicture[] {
  const editablePictures = flatMap(pages, (page) => page.editablePictures);
  const croppedPictures = filter(editablePictures, ({ cropping }) => {
    return cropping.default === false;
  });
  return croppedPictures;
}

export function findPagesWithEmptyEditablePictures(
  pages: ContentPage[],
  template: Template
): ContentPage[] {
  const pagesWithMissingPictures = filter(pages, (page) => {
    return hasEmptyEditablePictures(page, template);
  });
  return pagesWithMissingPictures;
}

export function findColorDefinition(
  colorDefinitionKey: string,
  colorDefinitions: TemplateColorDefinition[]
): TemplateColorDefinition | undefined {
  return colorDefinitions.find((colorDefinition) => colorDefinition.key === colorDefinitionKey);
}

export function findFontColorDefinition(
  fontColorKey: string,
  colorDefinition: TemplateBaseColorDefinition
): TemplateFontColorDefinition | undefined {
  if (!colorDefinition.fontColors) {
    return;
  }

  return colorDefinition.fontColors.find(
    (fontColorDefinition) => fontColorDefinition.key === fontColorKey
  );
}

export function findCellColorDefinition(
  cellColorKey: string,
  colorDefinition: TemplateColorDefinition
): TemplateCellColorDefinition | undefined {
  if (!colorDefinition.cellColors) {
    return;
  }

  return colorDefinition.cellColors.find(
    (cellColorDefinition) => cellColorDefinition.key === cellColorKey
  );
}

export function findPageDefinitionsWithValidCondition(
  template: Template,
  content: Content,
  useType?: { type?: string; filterType?: string },
  options?: OptionsConfiguration[]
): TemplatePageDefinition[] {
  const pageDefinitions = template.pageDefinitions;
  const validatedPageDefinitions = pageDefinitions.filter((pageDefinition) => {
    const typeCondition = useType?.type ? pageDefinition.type === useType.type : true;
    const filterTypeCondition = useType?.filterType
      ? pageDefinition.filterType === useType.filterType
      : true;
    const isValidCondition = pageDefinition.conditions
      ? evalContentCondition(pageDefinition.conditions, template, content, options)
      : true;
    return typeCondition && filterTypeCondition && isValidCondition;
  });

  return validatedPageDefinitions;
}

export function findValidPageDefinitionsToAdd(
  template: Template,
  content: Content,
  options?: OptionsConfiguration[]
): TemplatePageDefinition[] {
  const pageDefinitions = template.pageDefinitions.filter(
    ({ filterType, conditions }) =>
      filterType === template.contentPageConfiguration.addNext.filterType &&
      (!conditions || evalContentCondition(conditions, template, content, options))
  );

  return pageDefinitions || [];
}

export function findPagesWithInvalidCondition(
  template: Template,
  content: Content,
  options?: OptionsConfiguration[]
): ContentPage[] | null {
  const validatedPages = content.pages.filter((page) => {
    const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);
    const isValidCondition = pageDefinition.conditions
      ? evalContentCondition(pageDefinition.conditions, template, content, options)
      : true;
    return isValidCondition === false;
  });

  return validatedPages || null;
}
